import { Directive, ElementRef, inject, input, OnChanges, output, Renderer2 } from '@angular/core';
import { IConsumptionItem } from '@resident-nx/shared';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

@Directive({ standalone: true, selector: '[rsWebConsumptionChartYearA11y]' })
export class ConsumptionChartYearA11yDirective implements OnChanges {
  consumptionItemsWithFittingDatapointIndex = input<IConsumptionItem[]>();
  enableKeyboardNavigation = input<boolean>(true);
  #elementRef = inject(ElementRef);
  #renderer2 = inject(Renderer2);

  chartRerendered = input();

  dataPointSelectedByKeyboard = output<number>();

  ngOnChanges(): void {
    if (this.chartRerendered()) {
      this.makeBarsA11y();
    }
  }

  private makeBarsA11y(): void {
    this.adaptConsumptionBars();
    this.hideUnwantedAria();
  }

  private adaptConsumptionBars(): void {
    const selector = '.apexcharts-series[rel="2"]';
    const consumptionSeriesElements = this.#elementRef.nativeElement.querySelectorAll(selector);

    for (const series of consumptionSeriesElements) {
      const paths = series.querySelectorAll('path');
      for (const [index, path] of paths.entries()) {
        const barHeight = path.getAttribute('barHeight');

        if (barHeight > 0) {
          if (this.enableKeyboardNavigation()) {
            this.#renderer2.setAttribute(path, 'tabindex', '0');
          }

          const item = this.consumptionItemsWithFittingDatapointIndex()[index];
          const date = new Date(2023, index);
          const ariaMonth = format(date, 'LLLL', { locale: de }).toUpperCase();
          const ariaLabel = item ? `${ariaMonth} ${item.amount} ${item.unit}` : ariaMonth;
          this.#renderer2.setAttribute(path, 'aria-label', ariaLabel);
          this.#renderer2.setAttribute(path, 'role', 'button');
        }

        path.addEventListener('keydown', (event: KeyboardEvent) => {
          if (['Enter', 'Space'].includes(event.code)) {
            this.dataPointSelectedByKeyboard.emit(index);
          }
        });
      }
    }
  }

  private hideUnwantedAria(): void {
    const xAxis = this.#elementRef.nativeElement.querySelector('.apexcharts-xaxis');
    const yAxis = this.#elementRef.nativeElement.querySelector('.apexcharts-yaxis');
    this.#renderer2.setAttribute(xAxis, 'aria-hidden', 'true');
    this.#renderer2.setAttribute(yAxis, 'aria-hidden', 'true');
  }
}
